import { createApi } from '@reduxjs/toolkit/query/react';

import { networkBaseQueryV2 } from './queries';
import { contactsApi } from './contacts';
import deserialize from '../helpers/getroPropertyMapper';

export const sharedListViewsV2Api = createApi({
  reducerPath: 'sharedListViewsV2Api',
  baseQuery: networkBaseQueryV2,
  tagTypes: ['shared-list-view'],
  endpoints: (builder) => ({
    getSharedListViews: builder.query({
      query: ({ sharedListId }) => ({
        url: `/shared_lists/${sharedListId}/views`,
      }),
    }),
    getSharedListView: builder.query({
      query: ({ sharedListId, id }) => ({
        url: `/shared_lists/${sharedListId}/views/${id}`,
      }),
    }),
    createSharedListView: builder.mutation({
      query: ({ sharedListId, data }) => ({
        url: `/shared_lists/${sharedListId}/views`,
        method: 'POST',
        data,
      }),
      async onQueryStarted({ sharedListId }, { dispatch, queryFulfilled }) {
        try {
          const response = await queryFulfilled;
          const createdView = deserialize(response.data);

          dispatch(
            sharedListViewsV2Api.util.updateQueryData(
              'getSharedListViews',
              { sharedListId: `${sharedListId}` },
              (draft) => {
                draft.push(createdView);
              },
            ),
          );

          dispatch(
            contactsApi.util.updateQueryData('getSharedList', { listId: `${sharedListId}` }, (draft) => {
              if (!draft.sharedList) return;

              if (Array.isArray(draft.sharedList.views)) {
                draft.sharedList.views.push(createdView);
              } else {
                Object.assign(draft, {
                  sharedList: {
                    ...draft.sharedList,
                    views: [createdView],
                  },
                });
              }

              // Update shareable flag so the Share button updates immediately
              if (createdView.shareable) {
                Object.assign(draft.sharedList, { shareable: true });
              }
            }),
          );
        } catch {
          // Do nothing
        }
      },
    }),
    updateSharedListView: builder.mutation({
      query: ({ sharedListId, id, data }) => {
        const apiData = { ...data };

        if ('introRequestManager' in apiData) {
          delete apiData.introRequestManager;
        }

        return {
          url: `/shared_lists/${sharedListId}/views/${id}`,
          method: 'PATCH',
          data: apiData,
        };
      },
      async onQueryStarted({ sharedListId, id, data }, { dispatch, queryFulfilled }) {
        const viewsListPatch = dispatch(
          sharedListViewsV2Api.util.updateQueryData(
            'getSharedListViews',
            { sharedListId: `${sharedListId}` },
            (draft) => {
              const viewIndex = draft.findIndex((v) => v.id === id);
              if (viewIndex !== -1) {
                draft.splice(viewIndex, 1, { ...draft[viewIndex], ...data });
              }
            },
          ),
        );

        const singleViewPatch = dispatch(
          sharedListViewsV2Api.util.updateQueryData(
            'getSharedListView',
            { sharedListId: `${sharedListId}`, id: `${id}` },
            (draft) => ({
              ...draft,
              ...data,
            }),
          ),
        );

        const listDetailPatch = dispatch(
          contactsApi.util.updateQueryData('getSharedList', { listId: `${sharedListId}` }, (draft) => {
            if (!draft.sharedList?.views) return;
            const viewIndex = draft.sharedList.views.findIndex((v) => v.id === id);
            if (viewIndex !== -1) {
              const currentView = draft.sharedList.views[viewIndex];
              draft.sharedList.views.splice(viewIndex, 1, {
                ...currentView,
                ...data,
              });

              // Update passwordProtected flag so the Share button updates immediately
              if (currentView.shareable && typeof data.passwordProtected === 'boolean') {
                Object.assign(draft.sharedList, { passwordProtected: data.passwordProtected });
              }
            }
          }),
        );

        try {
          await queryFulfilled;
        } catch {
          viewsListPatch.undo();
          singleViewPatch.undo();
          listDetailPatch.undo();
        }
      },
    }),
    deleteSharedListView: builder.mutation({
      query: ({ sharedListId, id }) => ({
        url: `/shared_lists/${sharedListId}/views/${id}`,
        method: 'DELETE',
      }),
      async onQueryStarted({ sharedListId, id }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;

          dispatch(
            sharedListViewsV2Api.util.updateQueryData(
              'getSharedListViews',
              { sharedListId: `${sharedListId}` },
              (draft) => {
                const index = draft.findIndex((view) => view.id === id);
                if (index !== -1) {
                  draft.splice(index, 1);
                }
              },
            ),
          );

          dispatch(
            contactsApi.util.updateQueryData('getSharedList', { listId: `${sharedListId}` }, (draft) => {
              if (!draft.sharedList?.views) return;
              const index = draft.sharedList.views.findIndex((view) => view.id === id);
              if (index !== -1) {
                const deletedView = draft.sharedList.views[index];
                draft.sharedList.views.splice(index, 1);

                // Update shareable flag so the Share button updates immediately
                if (deletedView.shareable) {
                  Object.assign(draft.sharedList, { shareable: false, passwordProtected: false });
                }
              }
            }),
          );
        } catch {
          // Do nothing
        }
      },
    }),
  }),
});

export const {
  useGetSharedListViewsQuery,
  useLazyGetSharedListViewsQuery,
  useGetSharedListViewQuery,
  useLazyGetSharedListViewQuery,
  useCreateSharedListViewMutation,
  useUpdateSharedListViewMutation,
  useDeleteSharedListViewMutation,
} = sharedListViewsV2Api;
